/**
 * Badge Component
 *
 * A status badge component for displaying labels with semantic colors.
 * Supports success, warning, error, info, and neutral variants.
 *
 * Validates: Requirements 1.2, 1.3, 1.6
 * - Requirement 1.2: THE Console SHALL use modern UI patterns including cards
 *   with subtle shadows, rounded corners, and smooth transitions.
 * - Requirement 1.3: WHEN a user interacts with interactive elements, THE Console
 *   SHALL provide visual feedback within 100ms.
 * - Requirement 1.6: THE Console SHALL use accessible color contrasts meeting
 *   WCAG 2.1 AA standards for all text elements.
 */

import React, { forwardRef, useState } from 'react';
import { tokens } from '../tokens';

export type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'accent';
export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Semantic color variant */
  variant?: BadgeVariant;
  /** Size of the badge */
  size?: BadgeSize;
  /** Whether the badge has a dot indicator */
  dot?: boolean;
  /** Whether the badge is removable (shows X button) */
  removable?: boolean;
  /** Callback when remove button is clicked */
  onRemove?: () => void;
  /** Icon to display before the label */
  icon?: React.ReactNode;
}

/**
 * Get size-specific styles
 */
function getSizeStyles(size: BadgeSize): React.CSSProperties {
  switch (size) {
    case 'sm':
      return {
        padding: `${tokens.spacing[0]} ${tokens.spacing[2]}`,
        fontSize: tokens.typography.fontSize.xs,
        borderRadius: tokens.borderRadius.sm,
        minHeight: '20px',
      };
    case 'lg':
      return {
        padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
        fontSize: tokens.typography.fontSize.base,
        borderRadius: tokens.borderRadius.lg,
        minHeight: '32px',
      };
    case 'md':
    default:
      return {
        padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
        fontSize: tokens.typography.fontSize.sm,
        borderRadius: tokens.borderRadius.md,
        minHeight: '24px',
      };
  }
}

/**
 * Get variant-specific styles
 *
 * Color combinations meet WCAG 2.1 AA contrast requirements:
 * - Success: success-dark text on success-light background (contrast ratio > 4.5:1)
 * - Warning: warning-dark text on warning-light background (contrast ratio > 4.5:1)
 * - Error: error-dark text on error-light background (contrast ratio > 4.5:1)
 * - Info: info-dark text on info-light background (contrast ratio > 4.5:1)
 * - Neutral: neutral-700 text on neutral-100 background (contrast ratio > 4.5:1)
 */
function getVariantStyles(variant: BadgeVariant): {
  backgroundColor: string;
  color: string;
  dotColor: string;
} {
  switch (variant) {
    case 'success':
      return {
        backgroundColor: tokens.colors.success.light,
        color: tokens.colors.success.dark,
        dotColor: tokens.colors.success.main,
      };
    case 'warning':
      return {
        backgroundColor: tokens.colors.warning.light,
        color: tokens.colors.warning.dark,
        dotColor: tokens.colors.warning.main,
      };
    case 'error':
      return {
        backgroundColor: tokens.colors.error.light,
        color: tokens.colors.error.dark,
        dotColor: tokens.colors.error.main,
      };
    case 'info':
      return {
        backgroundColor: tokens.colors.info.light,
        color: tokens.colors.info.dark,
        dotColor: tokens.colors.info.main,
      };
    case 'accent':
      return {
        backgroundColor: tokens.colors.accent[50],
        color: tokens.colors.accent[700],
        dotColor: tokens.colors.accent[500],
      };
    case 'neutral':
    default:
      return {
        backgroundColor: tokens.colors.neutral[100],
        color: tokens.colors.neutral[700],
        dotColor: tokens.colors.neutral[500],
      };
  }
}

/**
 * Badge component for displaying status labels.
 *
 * @example
 * ```tsx
 * // Basic badges
 * <Badge variant="success">Active</Badge>
 * <Badge variant="warning">Pending</Badge>
 * <Badge variant="error">Failed</Badge>
 *
 * // Badge with dot indicator
 * <Badge variant="success" dot>Online</Badge>
 *
 * // Removable badge
 * <Badge variant="info" removable onRemove={() => console.log('removed')}>
 *   Tag
 * </Badge>
 *
 * // Badge with icon
 * <Badge variant="neutral" icon={<CheckIcon />}>
 *   Completed
 * </Badge>
 * ```
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  {
    variant = 'neutral',
    size = 'md',
    dot = false,
    removable = false,
    onRemove,
    icon,
    children,
    style,
    ...props
  },
  ref
) {
  const [isHovered, setIsHovered] = useState(false);

  const sizeStyles = getSizeStyles(size);
  const variantStyles = getVariantStyles(variant);

  const baseStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacing[1],
    fontFamily: tokens.typography.fontFamily.sans,
    fontWeight: tokens.typography.fontWeight.medium,
    lineHeight: tokens.typography.lineHeight.tight,
    whiteSpace: 'nowrap',
    backgroundColor: variantStyles.backgroundColor,
    color: variantStyles.color,
    // Transition for visual feedback within 100ms (using 150ms fast token)
    transition: `all ${tokens.transitions.fast}`,
    ...sizeStyles,
    ...style,
  };

  const dotStyles: React.CSSProperties = {
    width: size === 'sm' ? '6px' : size === 'lg' ? '10px' : '8px',
    height: size === 'sm' ? '6px' : size === 'lg' ? '10px' : '8px',
    borderRadius: tokens.borderRadius.full,
    backgroundColor: variantStyles.dotColor,
    flexShrink: 0,
  };

  const removeButtonStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size === 'sm' ? '14px' : size === 'lg' ? '20px' : '16px',
    height: size === 'sm' ? '14px' : size === 'lg' ? '20px' : '16px',
    marginLeft: tokens.spacing[1],
    marginRight: `-${tokens.spacing[1]}`,
    padding: 0,
    border: 'none',
    borderRadius: tokens.borderRadius.full,
    backgroundColor: isHovered ? variantStyles.color : 'transparent',
    color: isHovered ? variantStyles.backgroundColor : variantStyles.color,
    cursor: 'pointer',
    transition: `all ${tokens.transitions.fast}`,
    opacity: isHovered ? 1 : 0.7,
  };

  const iconStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  return (
    <span ref={ref} style={baseStyles} {...props}>
      {dot && <span style={dotStyles} aria-hidden="true" />}
      {icon && <span style={iconStyles}>{icon}</span>}
      {children}
      {removable && (
        <button
          type="button"
          style={removeButtonStyles}
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          aria-label="Remove"
        >
          <svg
            width={size === 'sm' ? '10' : size === 'lg' ? '14' : '12'}
            height={size === 'sm' ? '10' : size === 'lg' ? '14' : '12'}
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M3 3l6 6M9 3l-6 6" />
          </svg>
        </button>
      )}
    </span>
  );
});

export default Badge;
